import React, {Component} from "react";
import {Modal, ModalBody, ModalHeader} from 'reactstrap';
import PropTypes from 'prop-types';
import {observer} from "mobx-react";
import * as mobx from 'mobx';

import modalService from "../../../../components/modal/modalService";
import {PublishingDestinationSelector} from "../../PublishingDestinationSelector";
import {canApproveResponses} from "../postActions/ToolbarActions";
import UploadGalleryModal from "../../../../components/uploadgallery/UploadGalleryModal";
import {MediaAttachment} from "../../../../components/uploadgallery/MediaAttachment";
import {determineDefaultSelectionForReply} from "./determinePostSelection";

/**
 * component for writing replies to posts (not really a post editor, more like a reply creator)
 */
export const PostReplyEditor = observer(class PostReplyEditor extends Component {

  static propTypes = {
    post: PropTypes.object,
    onComplete: PropTypes.func,
    modalMode: PropTypes.bool,
    inResponseTo: PropTypes.object
  };

  state = {
    showErrors: false,
    selectedSocialAccount: null,
    text: '',
    attachment: {},
    loadingCredentials: true
  };

  accountId() {
    return this.props.post.accountId;
  }

  /**
   * Some providers let you choose other external users to reply with.
   * If this returns true, the PublishingDestinationSelector will be enabled
   **/
  canModifyAuthor() {
    return this.props.post.provider !== 'Google' && this.props.post.provider !== 'Instagram'
  }

  canAttachMedia() {
    return this.props.post.provider !== 'Google' && this.props.post.provider !== 'Instagram'
  }

  componentDidMount() {
    this.setState({
      loadingCredentials: false,
      selectedSocialAccount: determineDefaultSelectionForReply({post: this.props.post})
    })
  }

  editorErrors = () => {
    let errors = [];
    if (!this.state.selectedSocialAccount) {
      errors.push(
        this.canModifyAuthor()
          ? 'Select a page/user from the dropdown to publish with.'
          : 'No Credentials available to publish with'
      )
    }
    if (this.state.text.trim().length === 0 && !this.state.attachment.url) {
      errors.push('Add some content to the reply.')
    }
    if (this.props.post.provider === 'Twitter' &&  ((this.state.text || '') + (this.props.post.username || '')).length + 3 > 280) {
      errors.push('Reply cannot be over 280 characters (count includes the username of the user you are replying to)')
    }

    return errors;
  };

  setText = (text) => {
    this.setState({text});
  }

  openAttachmentModal = () => {
    modalService.requestModalV2({
      modal: <UploadGalleryModal 
        title="Select a Photo or Video"
        modalToggle={modalService.closeModal} 
        imageSelectedCallback={(x) => {
          modalService.closeModal();
          this.setState({attachment: {type: x.type, url: x.url}})
        }}
      />
    });
  };

  savePost = () => {
    const errors = this.editorErrors();
    if (errors.length === 0 && !this.state.isSaving) {
      const remotePageId =
        this.state.selectedSocialAccount.remoteId
          ? this.state.selectedSocialAccount.remoteId
          : this.state.selectedSocialAccount.remoteIds[0];

      let payload = {
        publishType: 'Reply',
        inReplyTo: this.props.post.postId,
        remotePageId,     // TODO: this should be changed to be publishTo, this is being fixed in the json formatter currently
        provider: this.props.post.provider,
        requiresApproval: !canApproveResponses(),
        details: {
          text: this.state.text,
          attachmentType: this.state.attachment.type,
          attachment: this.state.attachment.url,
        }
      };
      this.setState({isSaving: true});
      this.props.onComplete(payload);
    } else {
      this.setState({showErrors: true});
    }
  };

  setSelectedAccount = (selection) => {
    this.setState({
      selectedSocialAccount: selection,
      showErrors: false,
      isSaving: false,
      loadingCredentials: false
    });
  };

  render() {
    const errors = this.editorErrors();
    const isValid = errors.length === 0;

    const placeholderText = this.props.placeholder || "Type your response";

    const selected = this.state.selectedSocialAccount;
    const selectedArray = selected ? [selected] : [];
    return (
      <div className="content-spacer">
        <PublishingDestinationSelector
          provider={this.props.post.provider}
          excludeGroups={true}
          multi={false}
          modalMode={this.props.modalMode}
          accountId={this.accountId()}
          selected={selectedArray}
          onSelected={this.setSelectedAccount}
          isDisabled={!this.canModifyAuthor()}
        />

        <div
          className="chatter-publish-textarea-container content-spacer-padding-top"
        >
          <textarea
            placeholder={placeholderText}
            className="form-control"
            autoFocus
            value={this.state.text}
            onChange={e => this.setText(e.target.value)}
          />
          {this.renderAttachmentEditor()}
        </div>
        {!isValid && this.state.showErrors ? (
          <div
            className="alert alert-danger content-spacer-top"
          >
            Before you can reply:
            <ul>
              {errors.map(x => (
                <li key={x}>{x}</li>
              ))}
            </ul>
          </div>
        ) : (
          undefined
        )}
        <div className="clearfix content-spacer-top">
          <div className="pull-right">
            <button
              className="btn btn-sm btn-outline-secondary"
              onClick={() => this.props.onComplete()}
            >
              Cancel
            </button>
            <button
              className={`btn btn-sm btn-secondary control-spacer-left
                ${
                  this.state.isSaving || this.state.loadingCredentials
                    ? "disabled"
                    : ""
                }`}
              onClick={this.savePost}
            >
              {canApproveResponses() ? "Submit" : "Submit for Approval"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  renderAttachmentEditor() {
    if (!this.canAttachMedia()) {
      return <span />;
    } else {
      return (
        <div>
          {
            this.state.attachment.url
              ? (
                <span>
                  <MediaAttachment src={this.state.attachment.url} imageStyle={{maxWidth: '200px', maxHeight: '200px'}}/>
                  <a onClick={() => this.setState({attachment: {}})} className="ss ss-trash"/>
                </span>
              )
              : (
                <a className="attachment-link" onClick={this.openAttachmentModal}>
                  <span className="ss ss-camera"/>
                </a>
              )
          }
        </div>
      );
    }
  }
});
